import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../../components/button/button";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import { useParams } from "react-router";
import { InputBlock } from "./InputBlock";
import { useTextEditor } from "../../../components/textEditor/textEditor";
import { Spring } from "../../../components/spring/spring";
import { usePopup } from "../../../components/popup/popup";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import { PageLoader } from "../../../components/loader/pageLoader";
import { HTMLPreview } from "../../../components/htmlPreview/htmlPreview";
import { MediaSlider } from "../../../components/mediaSlider/mediaSlider";
import KnowledgeAPI from "../../../api/KnowledgeAPI";
import { ImageGrid } from "../../../components/imageGrid/imageGrid";
import { Scrollable } from "../../../components/scrollable/scrollable";

import styles from "./editMaterial.module.css";

import { Text } from "../../../components/text/text";

export const SuperadminEditMaterialPage = () => {
    const { cafeId, categoryId, materialId } = useParams();

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState(false);

    const [media, setMedia] = useState([]);
    const [oldImages, setOldImages] = useState([]);
    const [oldVideos, setOldVideos] = useState([]);

    const [editor, toHtml, setHtml] = useTextEditor();

    const [initLoading, setInitLoading] = useState(false);
    useEffect(() => {
        if (!materialId) return;

        setInitLoading(true);
        Promise.all([
            KnowledgeAPI.fetchAdminEducationMaterial(materialId),
            KnowledgeAPI.fetchAdminEducationMaterialVideos(materialId).catch((err) => {
                console.error(err);
                return [];
            })
        ])
            .then(([res, videos]) => {
                setName(res.title);
                setHtml(res.text);
                setOldImages(res.images);
                setOldVideos(videos);
            })
            .catch(console.error)
            .finally(() => setInitLoading(false));
    }, [materialId, setHtml]);

    useEffect(() => {
        setNameError(false);
    }, [name]);

    const validateData = () => {
        let error = false;

        if (name.length <= 1 || name.length > 100) {
            setNameError(true);
            error = true;
        }

        return error;
    }

    const navigate = useAnimatedNavigate()
    const [loading, setLoading] = useState(false);
    const onSave = async () => {
        if (validateData()) return;

        const images = Array.from(media).filter(el => el.type.startsWith("image/"));
        const videos = Array.from(media).filter(el => el.type.startsWith("video/"));

        setLoading(true);
        try {
            const res = await KnowledgeAPI.updateEducationMaterial(materialId, name, toHtml());

            if (media && media.length > 0) {
                await Promise.all(
                    [...videos, ...images].map(async (file) => {
                        if (file.type.startsWith("image/")) {
                            return KnowledgeAPI.addEducationMaterialImage(res.id, file);
                        }
                        return KnowledgeAPI.uploadMaterialVideo(res.id, file);
                    })
                );
            }

            if (cafeId) {
                navigate(`/superadmin/cafe/${cafeId}/knowledge/${categoryId}`);
            } else {
                navigate(`/superadmin/knowledge/${categoryId}`);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const [Popup, open] = usePopup();

    const [content, setContent] = useState(toHtml());
    const [previewMedia, setPreviewMedia] = useState([]);

    const revokePreviewMedia = useCallback(() => {
        previewMedia.forEach(url => {
            if (url?.src && url.src.startsWith('blob:')) {
                URL.revokeObjectURL(url.src);
            }
        });
    }, [previewMedia]);

    const onOpenDemo = () => {
        revokePreviewMedia();

        const images = Array.from(media).filter(el => el.type.startsWith("image/"));
        const videos = Array.from(media).filter(el => el.type.startsWith("video/"));

        const newUrls = [...videos, ...images].map(file => {
            if (file.type.startsWith("video/")) {
                return { type: "video", src: URL.createObjectURL(file) };
            }
            return { type: "image", src: URL.createObjectURL(file) };
        });

        setPreviewMedia(newUrls);
        setContent(toHtml());
        open();
    };

    useEffect(() => {
        return revokePreviewMedia;
    }, [revokePreviewMedia]);

    const onDeleteOldMedia = (item) => {
        if (!item?.id) return;
        if (item.type === "video") {
            KnowledgeAPI
                .deleteEducationMaterialVideo(item.id)
                .then(() => setOldVideos(prev => prev.filter(el => el.id !== item.id)))
                .catch(console.error);
        } else {
            KnowledgeAPI
                .deleteEducationMaterialImage(item.id)
                .then(() => setOldImages(prev => prev.filter(el => el.id !== item.id)))
                .catch(console.error);
        }
    }

    const oldMediaForGrid = useMemo(() => {
        return [
            ...oldVideos.map(v => ({ id: v.id, src: v.src, type: "video" })),
            ...oldImages.map(img => ({ id: img.id, src: img.src, type: "image" })),
        ];
    }, [oldImages, oldVideos]);

    const allMedia = useMemo(() => {
        const old = [
            ...oldVideos.map(v => ({ type: "video", src: v.src })),
            ...oldImages.map(img => ({ type: "image", src: img.src })),
        ];
        return old.concat(previewMedia);
    }, [oldImages, oldVideos, previewMedia]);

    if (initLoading) {
        return <PageLoader />
    }

    return (
        <>
            <AdminHeader title="Изменить материал" />

            <InputBlock
                type="text"
                title="Введите название материала"
                value={name}
                setValue={setName}
                error={nameError}
            />

            {
                oldMediaForGrid && oldMediaForGrid.length > 0 &&
                <div className={styles.input_block}>
                    <Text as="div" variant="h3" className={styles.input_block_title} size={12} weight={400} lineHeight="1">Ранее загруженные изображения и видео</Text>
                    <ImageGrid
                        images={oldMediaForGrid}
                        onDeleteItem={onDeleteOldMedia}
                        deleteEntityName={(item) => (item?.type === "video" ? "видео" : "изображение")}
                    />
                </div>
            }

            <InputBlock
                type="files"
                title="Выберите изображения или видео для добавления в материал"
                value={media}
                setValue={setMedia}
                accept="image/*, video/*"
            />

            <InputBlock
                type="textEditor"
                title="Введите текст материала"
                editor={editor}
            />

            <Button
                type="secondary"
                text="Предпросмотр"
                onClick={onOpenDemo}
            />

            <Spring />

            <Button
                text="Сохранить"
                onClick={onSave}
                disabled={loading}
            />

            <Popup style={{ height: "100%", maxHeight: "90dvh" }}>
                <Scrollable className={styles.preview}>
                    <Text as="div" variant="h1" className={styles.material_title} >{name}</Text>
                    {
                        allMedia && allMedia.length > 0 &&
                        <MediaSlider media={allMedia} />
                    }
                    <HTMLPreview content={content} />
                </Scrollable>
            </Popup>
        </>
    )
}
